import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RudrakshaTransp from '../assets/rudraksha transp.png';
import './componentDarkSurfaceFixes.css';
import './SeriousSeekerSection.css';

type SeriousSeekerCard = {
  numeral: string;
  titleKey: string;
  descKey: string;
  icon: React.ReactNode;
};

const ShieldIcon = () => (
  <svg className="sl-ss-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 3.35 18.85 6v5.28c0 4.16-2.71 7.88-6.85 9.37-4.14-1.49-6.85-5.21-6.85-9.37V6L12 3.35Z"
      vectorEffect="non-scaling-stroke"
    />
    <path
      className="sl-ss-icon-check"
      d="m8.5 12.1 2.35 2.35 4.95-5.05"
      pathLength={1}
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const LayersIcon = () => (
  <svg className="sl-ss-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <ellipse className="sl-ss-layer-top" cx="12" cy="6.9" rx="6.9" ry="3.15" vectorEffect="non-scaling-stroke" />
    <path
      className="sl-ss-layer-mid"
      d="M18.9 11.15c0 1.74-3.09 3.15-6.9 3.15s-6.9-1.41-6.9-3.15"
      vectorEffect="non-scaling-stroke"
    />
    <path
      className="sl-ss-layer-bot"
      d="M18.9 15.35c0 1.74-3.09 3.15-6.9 3.15s-6.9-1.41-6.9-3.15"
      vectorEffect="non-scaling-stroke"
    />
    <path d="M5.1 6.9v8.45M18.9 6.9v8.45" vectorEffect="non-scaling-stroke" />
  </svg>
);

const SeekersIcon = () => (
  <svg className="sl-ss-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle className="sl-ss-person-1" cx="8.75" cy="8.35" r="2.55" vectorEffect="non-scaling-stroke" />
    <path
      className="sl-ss-person-1"
      d="M4.25 18.75c.55-3.35 2.08-5.03 4.5-5.03s3.95 1.68 4.5 5.03"
      vectorEffect="non-scaling-stroke"
    />
    <circle className="sl-ss-person-2" cx="15.7" cy="9.35" r="2.15" vectorEffect="non-scaling-stroke" />
    <path
      className="sl-ss-person-2"
      d="M12.55 18.75c.42-2.62 1.55-3.94 3.4-3.94 1.84 0 2.97 1.32 3.4 3.94"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const GlobeIcon = () => (
  <svg className="sl-ss-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="12" cy="12" r="8.45" vectorEffect="non-scaling-stroke" />
    <path d="M3.55 12h16.9M5.65 7.45h12.7M5.65 16.55h12.7" vectorEffect="non-scaling-stroke" />
    <ellipse className="sl-ss-globe-meridian" cx="12" cy="12" rx="3.3" ry="8.45" vectorEffect="non-scaling-stroke" />
  </svg>
);

const seriousSeekerCards: SeriousSeekerCard[] = [
  {
    numeral: 'I',
    titleKey: 'features.serious_seeker_authenticity_title',
    descKey: 'features.serious_seeker_authenticity_desc',
    icon: <ShieldIcon />,
  },
  {
    numeral: 'II',
    titleKey: 'features.serious_seeker_depth_title',
    descKey: 'features.serious_seeker_depth_desc',
    icon: <LayersIcon />,
  },
  {
    numeral: 'III',
    titleKey: 'features.serious_seeker_insider_title',
    descKey: 'features.serious_seeker_insider_desc',
    icon: <SeekersIcon />,
  },
  {
    numeral: 'IV',
    titleKey: 'features.serious_seeker_holistic_title',
    descKey: 'features.serious_seeker_holistic_desc',
    icon: <GlobeIcon />,
  },
];

const SeriousSeekerSection = () => {
  const { t } = useTranslation('features');
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -80px 0px',
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className={`sl-serious-seeker-surface relative bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 py-16 sm:py-24 overflow-hidden ${
        inView ? 'sl-ss-in-view' : ''
      }`}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23999%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-30"></div>
      {/* Rudraksha decorative image */}
      <img
        src={RudrakshaTransp}
        alt="Rudraksha decorative"
        className="sl-serious-seeker-image absolute top-0 left-0 w-[400px] z-10 pointer-events-none select-none"
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="relative z-20 mx-auto max-w-2xl lg:text-center">
          <div className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/30 px-4 py-2 text-sm font-medium text-blue-800 dark:text-blue-400 ring-1 ring-blue-200 dark:ring-blue-800 mb-4">
            <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {t('features.serious_seeker_badge')}
          </div>
          <h2 className="text-3xl font-semibold tracking-[-0.024em] text-gray-900 dark:text-white sm:text-4xl">
            {t('features.serious_seeker_title')}
          </h2>
          <p className="mt-6 text-[17px] leading-[1.7] text-slate-600 dark:text-gray-300 max-w-3xl mx-auto">
            {t('features.serious_seeker_description')}
          </p>
        </div>

        <div className="relative z-20 mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-5xl">
          <div className="sl-ss-grid grid grid-cols-1 gap-5 lg:grid-cols-2">
            {seriousSeekerCards.map((card) => (
              <article className="sl-ss-card" key={card.numeral}>
                <div className="sl-ss-card-head">
                  <span className="sl-ss-numeral" aria-hidden="true">
                    {card.numeral}
                  </span>
                  <span className="sl-ss-rule-thin" aria-hidden="true" />
                  {card.icon}
                </div>
                <h3 className="sl-ss-card-title">{t(card.titleKey)}</h3>
                <div className="sl-ss-accent" aria-hidden="true" />
                <p className="sl-ss-card-body">{t(card.descKey)}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeriousSeekerSection;
